import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { ApiErrorProvider } from "../../../components/ReactHookForm/ApiError";
import { apiGetErrorDetail } from "../../../helpers/api";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

/**
 * @typedef {object} SubscriptionData
 * @property {string} subscriptionId
 * @property {string[]} holderIds
 */

/**
 * @typedef {SubscriptionData[]} SubscriptionsData
 */

/**
 * @typedef {Object} Props
 * @property {string} [yearId]
 * @property {string} customerId
 * @property {(params: {invoice: import("../../../types/Invoice").Invoice}) => void} onSuccess
 * @property {SubscriptionsData} [initialSubscriptionsData]
 * @property {string} [localStorageKey]
 * @property {(props: { subscriptionsData: SubscriptionsData, upsertSubscriptionData: (params: {subscriptionId: string, holderIds: string[]}) => void, deleteSubscriptionData: (params: {subscriptionId: string}) => void, submit: () => void, isLoading: boolean }) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function SubscriptionBatchHandler({
  yearId,
  customerId,
  onSuccess,
  initialSubscriptionsData = [],
  localStorageKey,
  render,
}) {
  const intl = useIntl();

  const { request, isLoading, toastSuccess, toastError } = useRequest();

  const [subscriptionsData, setSubscriptionsData] = useState(
    initialSubscriptionsData,
  );

  const upsertSubscriptionData = useCallback(
    /**
     * @param {object} params
     * @param {string} params.subscriptionId
     * @param {string[]} params.holderIds
     */
    ({ subscriptionId, holderIds }) => {
      setSubscriptionsData((prevSubscriptionsData) => {
        const subscriptionIndex = prevSubscriptionsData.findIndex(
          (subscription) => subscription.subscriptionId === subscriptionId,
        );

        if (subscriptionIndex === -1) {
          return [...prevSubscriptionsData, { subscriptionId, holderIds }];
        } else {
          return [
            ...prevSubscriptionsData.slice(0, subscriptionIndex),
            { subscriptionId, holderIds },
            ...prevSubscriptionsData.slice(subscriptionIndex + 1),
          ];
        }
      });
    },
    [],
  );

  const deleteSubscriptionData = useCallback(
    /**
     * @param {object} params
     * @param {string} params.subscriptionId
     */
    ({ subscriptionId }) => {
      setSubscriptionsData((prevSubscriptionsData) =>
        prevSubscriptionsData.filter(
          (subscription) => subscription.subscriptionId !== subscriptionId,
        ),
      );
    },
    [],
  );

  const submit = useCallback(() => {
    /**
     * @type {{
     * data: {year_id: string | undefined,
     * customer_id: string | undefined,
     * subscriptions: {subscription_id: string, holder_id?: string[]}[]}
     * }}
     */
    const payload = {
      data: {
        year_id: yearId,
        customer_id: customerId,
        subscriptions: subscriptionsData.map((subscriptionData) => ({
          subscription_id: subscriptionData.subscriptionId,
          holder_id: subscriptionData.holderIds,
        })),
      },
    };
    request(
      generateApiUri({
        id: "@subscriptions.customers.batch",
        query: {
          fields: ["invoice.services"],
        },
      }),
      {
        method: "POST",
        body: payload,
      },
    )
      .then((response) => {
        onSuccess({ invoice: response.data });
        toastSuccess({
          title: "Les modifications ont bien été prises en compte.",
        });
        if (localStorageKey) {
          localStorage.setItem(localStorageKey, response.data.id);
        }
      })
      .catch((response) => {
        const message = apiGetErrorDetail({ error: response });
        toastError({
          title: intl.formatMessage({
            defaultMessage: "Une erreur est survenue.",
          }),
          description: message,
        });
      });
  }, [
    yearId,
    customerId,
    subscriptionsData,
    request,
    onSuccess,
    toastSuccess,
    localStorageKey,
    toastError,
    intl,
  ]);

  return (
    <ApiErrorProvider>
      {render({
        subscriptionsData,
        upsertSubscriptionData,
        deleteSubscriptionData,
        submit,
        isLoading,
      })}
    </ApiErrorProvider>
  );
}

export default SubscriptionBatchHandler;
